import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Form, Tabs, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { getCurrentUser, updatePassword } from '../services/authService'
import { updateUserProfile } from '../services/userService'
import { UserResponse } from '../types/user'
import { PageLayout } from '../components/layout'
import { ProfileInfo, ProfileForm, PasswordModal } from '../components/profile'
import { handleApiError } from '../utils/errorHandler'

const BASIC_TAB_KEY = '1'
const EDIT_TAB_KEY = '2'

const Profile: React.FC = () => {
    const { t } = useTranslation(['profile', 'common'])
    const [user, setUser] = useState<UserResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<string | null>(null)
    const [showPasswordModal, setShowPasswordModal] = useState(false)
    const [activeTab, setActiveTab] = useState(BASIC_TAB_KEY)
    const [messageApi, contextHolder] = message.useMessage()
    const submittingRef = useRef(false)

    const [form] = Form.useForm()
    const [passwordForm] = Form.useForm()

    const fetchUser = useCallback(async () => {
        try {
            const userData = await getCurrentUser()
            setUser(userData)
            if (userData) {
                form.setFieldsValue({
                    username: userData.username,
                })
            }
        } catch {
            setError(t('profile:basic.fetchError'))
        } finally {
            setLoading(false)
        }
    }, [form, setUser, setError, setLoading, t])

    useEffect(() => {
        fetchUser()
    }, [fetchUser])

    const resetProfileForm = useCallback(() => {
        form.resetFields(['username'])
        if (user) {
            form.setFieldsValue({
                username: user.username,
            })
        }
        form.setFields([
            {
                name: 'username',
                errors: [],
                warnings: [],
            },
        ])
    }, [form, user])

    const handleTabChange = useCallback(
        (nextActiveKey: string) => {
            if (activeTab === EDIT_TAB_KEY && nextActiveKey !== EDIT_TAB_KEY) {
                resetProfileForm()
            }
            setActiveTab(nextActiveKey)
        },
        [activeTab, resetProfileForm]
    )

    const handleOpenPasswordModal = useCallback(() => {
        passwordForm.resetFields()
        setShowPasswordModal(true)
    }, [passwordForm])

    const handleClosePasswordModal = useCallback(() => {
        setShowPasswordModal(false)
        passwordForm.resetFields()
    }, [passwordForm])

    const handleUpdateProfile = async (values: {
        username: string
    }) => {
        if (!user) return
        if (submittingRef.current) return

        submittingRef.current = true
        setLoading(true)
        setError(null)
        setSuccess(null)

        try {
            await updateUserProfile(user.id, values)
            const successMsg = t('profile:basic.updateSuccess')
            setSuccess(successMsg)
            messageApi.success(successMsg)
            setUser({
                ...user,
                username: values.username,
            })
        } catch (error: unknown) {
            const errorMessage = handleApiError(error).message || t('profile:basic.updateError')
            messageApi.error(errorMessage)
        } finally {
            setLoading(false)
            submittingRef.current = false
        }
    }

    const handleUpdatePassword = async (values: {
        current_password: string
        new_password: string
    }) => {
        if (submittingRef.current) return

        submittingRef.current = true
        setLoading(true)
        setError(null)
        setSuccess(null)

        try {
            await updatePassword(values.current_password, values.new_password)
            const successMsg = t('profile:security.passwordChanged')
            setSuccess(successMsg)
            messageApi.success(successMsg)
            handleClosePasswordModal()
        } catch (error: unknown) {
            const errorMessage =
                handleApiError(error).message || t('profile:security.passwordError')
            messageApi.error(errorMessage)
        } finally {
            setLoading(false)
            submittingRef.current = false
        }
    }

    const items = [
        {
            key: BASIC_TAB_KEY,
            label: t('profile:tabs.basic'),
            children: (
                <ProfileInfo
                    user={user}
                    error={error}
                    success={success}
                    loading={loading}
                    onClearSuccess={() => setSuccess(null)}
                    onClearError={() => setError(null)}
                    onShowPasswordModal={handleOpenPasswordModal}
                />
            ),
        },
        {
            key: EDIT_TAB_KEY,
            label: t('profile:tabs.edit'),
            children: (
                <ProfileForm
                    form={form}
                    loading={loading}
                    onSubmit={handleUpdateProfile}
                />
            ),
        },
    ]

    if (loading && !user) {
        return <div style={{ textAlign: 'center', padding: 50 }}>{t('profile:loading')}</div>
    }

    return (
        <PageLayout title={t('profile:title')}>
            {contextHolder}
            <Tabs activeKey={activeTab} onChange={handleTabChange} items={items} />

            <PasswordModal
                visible={showPasswordModal}
                loading={loading}
                form={passwordForm}
                onCancel={handleClosePasswordModal}
                onSubmit={handleUpdatePassword}
            />
        </PageLayout>
    )
}

export default Profile
